import type { Category } from './categoryTree'

/**
 * The categories a delete may move transactions to (#285, #588): active, not
 * the category itself, and not any descendant (the backend answers 422 to both).
 *
 * A household category (`household_id` set) may only move to a system category
 * or one of the same household (backend#478/#483). A personal category
 * (`household_id` null, not system) keeps every category the caller can see:
 * the backend accepts system, own and active-household targets. The inline 422
 * stays as the backstop for membership changing while the picker is open.
 */
export function deleteTargets(category: Category, all: readonly Category[]): Category[] {
  const excluded = new Set<string>([category.id])
  // Transitive, though the UI builds two levels: the exclusion must not depend on that.
  let grew = true
  while (grew) {
    grew = false
    for (const c of all) {
      if (c.parent_id != null && excluded.has(c.parent_id) && !excluded.has(c.id)) {
        excluded.add(c.id)
        grew = true
      }
    }
  }
  const house = category.household_id ?? null
  const isSystem = (c: Category) => c.is_system && (c.household_id ?? null) === null
  const scoped = (c: Category) => house === null || isSystem(c) || c.household_id === house
  return all.filter((c) => c.is_active && !excluded.has(c.id) && scoped(c))
}

/** The parent when it is a valid target, else `null` (Uncategorized). */
export function defaultTarget(category: Category, targets: readonly Category[]): string | null {
  const parent = category.parent_id
  return parent != null && targets.some((t) => t.id === parent) ? parent : null
}
